import { useState } from "react";
import { Star, MessageSquare, Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { BUSINESS } from "@/lib/constants";
import { timeAgo } from "@/lib/leadFormat";

const REVIEW_URL = `${BUSINESS.siteUrl}/leave-a-review`;

// One-tap review invite for won jobs. Opens John's own SMS/email app with a
// pre-written message — the server sends nothing, it only records the tap.
export default function ReviewInviteCard({ lead, onChange }) {
    const [busy, setBusy] = useState(false);
    const first = (lead.name || "").trim().split(" ")[0];
    const message = `Hi${first ? ` ${first}` : ""}, it's John from A1 Screening. Thanks for letting us take care of your screen work! If you have a minute, we'd really appreciate a quick review: ${REVIEW_URL} — thank you!`;

    const mark = async (channel) => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/admin/leads/${lead.id}/review-invite`, { channel });
            onChange?.();
        } catch { /* non-blocking bookkeeping */ } finally {
            setBusy(false);
        }
    };

    const sent = lead.review_invite;

    return (
        <div
            className="a1-card mb-4"
            style={{ borderLeft: "4px solid var(--color-cta, #fcd64b)" }}
            data-testid="review-invite-card"
        >
            <div className="flex items-center gap-2 mb-1">
                <Star size={18} style={{ color: "var(--color-cta, #fcd64b)" }} />
                <h3 className="text-base m-0">Job won — ask for a review</h3>
            </div>
            {sent ? (
                <p className="text-sm m-0 mb-3 flex items-center gap-1.5" style={{ color: "var(--color-success)" }} data-testid="review-invite-status">
                    <CheckCircle2 size={14} /> Review invite sent {timeAgo(sent.sent_at)} via {sent.channel}. You can send it again if needed.
                </p>
            ) : (
                <p className="text-sm text-[var(--color-text-muted)] m-0 mb-3">
                    One tap opens your own messages with this pre-written invite — review it, then hit send.
                </p>
            )}
            <p
                className="text-sm italic leading-relaxed rounded-md px-3 py-2 mb-3"
                style={{ background: "var(--color-bg-alt)", border: "1px solid var(--color-line)", color: "var(--color-text-muted)" }}
                data-testid="review-invite-preview"
            >
                “{message}”
            </p>
            <div className="flex flex-wrap gap-2">
                <a
                    href={`sms:${lead.phone}?&body=${encodeURIComponent(message)}`}
                    className="a1-btn-primary"
                    onClick={() => mark("sms")}
                    data-testid="review-invite-sms"
                >
                    <MessageSquare size={15} /> Text review invite
                </a>
                {lead.email ? (
                    <a
                        href={`mailto:${lead.email}?subject=${encodeURIComponent("Thanks from A1 Screening")}&body=${encodeURIComponent(message)}`}
                        className="a1-btn-secondary"
                        onClick={() => mark("email")}
                        data-testid="review-invite-email"
                    >
                        <Mail size={15} /> Email invite
                    </a>
                ) : null}
            </div>
        </div>
    );
}
